// src/pages/payment/PaymentDetailPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Row, Col, Card, CardBody, CardHeader, Badge, Button, Alert, Spinner,
    UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
    fetchPaymentById,
    fetchPaymentRefundDetail,
    fetchPaymentContract,
    updatePaymentAccounted,
} from "../../api/requests/payment-requests";
import { fetchAuthMe } from "../../api/requests/auth-requests";
import { viewPaymentReceipt, downloadPaymentReceiptPdf } from "./paymentReceipt";
import PaymentRefundModal from "../modals/payment/PaymentRefundModal";
import PaymentReceiptEmailModal from "../modals/payment/PaymentReceiptEmailModal";
import { CARD_BRAND_BANK_LABELS } from "../../utils/paymentLinkUtils";
import { RECEIPT_TYPE_BADGE_COLORS, RECEIPT_TYPE_LABEL_MAP } from "../../constants/companyTypes";
import { ACCOUNT_TYPE_LABEL_MAP } from "../../constants/accountTypes";
import { getPaymentSourceText } from "../Tables/Paymenthistoryutils";
import "../../assets/css/page-common.css";
import BackButton from "../../components/Common/BackButton";

/* page-common.css tasarım dilinin renkleri (SubscriptionDetailPage vb. ile aynı). */
const NAVY = "#221C5D";
const GREEN = "#71C30B";
const ORANGE = "#E56A22";
const RED = "#dc3545";
const GRAY = "#64748b";

/* İade ucu olan sağlayıcılar — ödeme geçmişi listesindeki kuralın aynısı. */
const REFUNDABLE_VPOS = ["PAYTR", "GARANTI", "ISYERIM_POS"];

const formatDateTime = (date) =>
    date
        ? new Date(date).toLocaleString("tr-TR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        })
        : "-";

const formatAmountTRY = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    const n = typeof val === "string" ? parseFloat(val) : val;
    if (isNaN(n)) return "-";
    return `₺ ${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatForeignAmount = (amount, currency) => {
    if (amount === null || amount === undefined || !currency) return "-";
    const n = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(n)) return "-";
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(n);
};

const getStatusInfo = (status) => {
    switch (status) {
        case "COMPLETED":
        case "SUCCESS":
            return { color: "success", hex: GREEN, text: "Tamamlandı", icon: "bx-check-circle" };
        case "PENDING":
        case "WAITING":
            return { color: "warning", hex: ORANGE, text: "Beklemede", icon: "bx-hourglass" };
        case "FAILED":
            return { color: "danger", hex: RED, text: "Başarısız", icon: "bx-x-circle" };
        case "CANCELLED":
            return { color: "dark", hex: GRAY, text: "İptal Edildi", icon: "bx-time" };
        // İade durumları eksikti; detay sayfasında ham "REFUNDED" görünüyordu.
        // Etiketler Paymenthistoryconstants.js'teki PAYMENT_STATUS ile aynı.
        case "REFUNDED":
            return { color: "secondary", hex: GRAY, text: "İade Edildi", icon: "bx-undo" };
        case "PARTIALLY_REFUNDED":
            return { color: "warning", hex: ORANGE, text: "Kısmi İade", icon: "bx-undo" };
        default:
            return { color: "secondary", hex: GRAY, text: status || "-", icon: "bx-dots-horizontal-rounded" };
    }
};

/**
 * Kart markası etiketleri. Banka adıyla birlikte gösterilir ("Garanti BBVA Bonus"):
 * kullanıcı kartını çoğunlukla bankasıyla anıyor, yalın "Bonus" daha az anlaşılır.
 * Kaynak map ödeme linki tarafında zaten tanımlı; tek yerden okunuyor.
 */
const getCardBrandTr = (brand) => CARD_BRAND_BANK_LABELS[brand] || brand || "-";

const getCardTypeTr = (t) => ({ CREDIT: "Kredi Kartı", DEBIT: "Banka Kartı" }[t] || t || "-");

/* ─────────────────────────── Küçük bileşenler ─────────────────────────── */

/** page-common.css'teki detail-card + section-title kabuğu. */
const Section = ({ icon, title, color = NAVY, right, children, className = "" }) => (
    <Card className={`detail-card mb-3 ${className}`}>
        <CardHeader className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
            <div className="section-title">
                <div className="section-icon" style={{ background: `${color}1A`, color }}>
                    <i className={`bx ${icon}`} />
                </div>
                {title}
            </div>
            {right}
        </CardHeader>
        <CardBody>{children}</CardBody>
    </Card>
);

/**
 * Etiket + değer kutusu. Değer boşsa satır HİÇ çizilmez: ödemelerin çoğunda
 * fatura/cari alanlarının bir kısmı dolu değil ve boş "-" kutuları kartı
 * gereksiz uzatıp dolu alanların okunmasını zorlaştırıyordu.
 */
const Field = ({ label, value, children, xs = 6, md = 4, className = "" }) => {
    const content = children !== undefined ? children : value;
    const empty = content === null || content === undefined || content === "";
    if (empty) return null;
    return (
        <Col xs={xs} md={md} className={`info-field ${className}`.trim()}>
            <div className="info-label">{label}</div>
            <div className="info-value">{content}</div>
        </Col>
    );
};

/** İşlem kodu gibi kopyalanacak teknik değerler. */
const CopyField = ({ value }) => {
    const [copied, setCopied] = useState(false);
    if (!value) return null;
    const onCopy = () => {
        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };
    return (
        <span className="d-inline-flex align-items-center gap-2">
            <span className="font-monospace text-break">{value}</span>
            <button type="button" className="btn btn-sm btn-link p-0" onClick={onCopy} title="Kopyala">
                <i className={`bx ${copied ? "bx-check text-success" : "bx-copy"}`} />
            </button>
        </span>
    );
};

/** Boş kart yerine tek satırlık açıklama. */
const EmptyHint = ({ children }) => (
    <div className="text-muted" style={{ fontSize: ".85rem" }}>{children}</div>
);

/* ──────────────────────────────── Sayfa ──────────────────────────────── */

const PaymentDetailPage = ({ payment: paymentProp, onBack }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const statePayment = location?.state?.payment;

    const [payment, setPayment] = useState(statePayment || paymentProp || null);
    const [loading, setLoading] = useState(!statePayment && !paymentProp && !!id);
    const [error, setError] = useState("");

    const resolvedPaymentId = payment?.id || id;

    /**
     * Ödemeyi uçtan tazeler. İlk açılışta (listeden gelinmediyse) ve iade /
     * muhasebeleştirme sonrası çağrılır: aksiyonlar ödemenin durumunu ve iade
     * edilebilir kalan tutarını değiştiriyor.
     */
    const reloadPayment = useCallback(async () => {
        if (!resolvedPaymentId) return;
        try {
            const res = await fetchPaymentById(resolvedPaymentId);
            if (res?.statusCode === 200 && res.data) setPayment(res.data);
        } catch {
            /* ekrandaki veri korunur */
        }
    }, [resolvedPaymentId]);

    useEffect(() => {
        document.title = "Ödeme Detayı | Tahsilat360";
        let alive = true;
        const run = async () => {
            if (payment || !id) return;
            try {
                setLoading(true);
                setError("");
                const res = await fetchPaymentById(id);
                if (!alive) return;
                if (res?.statusCode === 200) {
                    setPayment(res.data || null);
                } else {
                    setError(res?.message || "Ödeme detayı alınamadı.");
                }
            } catch (e) {
                if (!alive) return;
                setError("Ödeme detayı alınamadı.");
            } finally {
                if (alive) setLoading(false);
            }
        };
        run();
        return () => {
            alive = false;
        };
    }, [id]); // paymentProp/statePayment zaten set edildi

    // İade geçmişi ayrı uçtan gelir: ödeme DTO'su iade SATIRLARINI taşımaz
    // (taşısaydı liste ekranında satır başına ek sorgu gerekirdi).
    const [refundDetail, setRefundDetail] = useState(null);

    const reloadRefundDetail = useCallback(async () => {
        if (!resolvedPaymentId) return;
        try {
            const res = await fetchPaymentRefundDetail(resolvedPaymentId);
            if (res?.statusCode === 200) setRefundDetail(res.data);
        } catch {
            /* iade bölümü gizlenir, sayfanın geri kalanı çalışır */
        }
    }, [resolvedPaymentId]);

    useEffect(() => { reloadRefundDetail(); }, [reloadRefundDetail]);

    // Aksiyon yetkileri ödeme geçmişi listesindekiyle aynı rollere bağlı.
    const [currentUser, setCurrentUser] = useState(null);
    useEffect(() => {
        let alive = true;
        fetchAuthMe()
            .then(async (res) => {
                if (!res?.ok) return;
                const { data } = await res.json();
                if (alive) setCurrentUser(data);
            })
            .catch(() => { /* yetki çözülemezse aksiyonlar gizli kalır */ });
        return () => { alive = false; };
    }, []);

    const roles = currentUser?.roles || [];
    const canToggleAccounted = roles.some((r) =>
        ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT"].includes(r));
    const canRefund = canToggleAccounted;
    const isRefundable =
        !!payment
        && (payment.status === "SUCCESS" || payment.status === "PARTIALLY_REFUNDED")
        && REFUNDABLE_VPOS.includes(payment.virtualPos);

    /* Sözleşme metni ayrı uçtan ve YALNIZCA kart açıldığında çekilir: metin
       kilobaytlarca olabiliyor, sayfa her açıldığında indirmek gereksiz.
       "Görüntüle"ye basılınca bir kez yüklenir, sonra açılıp kapanır. */
    const [contract, setContract] = useState(null);
    const [contractOpen, setContractOpen] = useState(false);
    const [contractLoading, setContractLoading] = useState(false);

    const toggleContract = async () => {
        if (contractOpen) { setContractOpen(false); return; }
        setContractOpen(true);
        if (contract || contractLoading) return;
        setContractLoading(true);
        try {
            const res = await fetchPaymentContract(resolvedPaymentId);
            setContract(res?.statusCode === 200 ? (res.data || {}) : {});
        } catch {
            setContract({});
        } finally {
            setContractLoading(false);
        }
    };

    /* ── Aksiyonlar ── */
    const [pdfLoading, setPdfLoading] = useState(false);
    const [accountingLoading, setAccountingLoading] = useState(false);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [refundModalOpen, setRefundModalOpen] = useState(false);

    const handleDownloadPdf = async () => {
        setPdfLoading(true);
        try {
            await downloadPaymentReceiptPdf(payment);
        } finally {
            setPdfLoading(false);
        }
    };

    const handleToggleAccounted = async () => {
        const newValue = !payment.accounted;
        setAccountingLoading(true);
        try {
            await updatePaymentAccounted(payment.id, newValue);
            setPayment((prev) => (prev ? { ...prev, accounted: newValue } : prev));
            toast.success(newValue ? "Ödeme muhasebeleştirildi." : "Ödeme muhasebeden çıkarıldı.");
        } catch {
            toast.error("Muhasebe durumu güncellenemedi.");
        } finally {
            setAccountingLoading(false);
        }
    };

    // İade sonrası hem ödemenin durumu/kalan tutarı hem de iade satırları değişir.
    const handleRefundDone = () => {
        reloadPayment();
        reloadRefundDetail();
    };

    const installmentText = useMemo(() => {
        if (!payment || payment.receiptType === "CASH") {
            return "-";
        }
        return payment.installmentCount > 1
            ? `${payment.installmentCount} taksit (aylık ${formatAmountTRY((payment.amount || 0) / payment.installmentCount)})`
            : "1 (Tek Çekim)";
    }, [payment]);

    const styles = `
    .payment-detail .amount-hero {
      display:flex; align-items:center; justify-content:space-between; gap:1rem; flex-wrap:wrap;
      border-radius:14px; padding:1rem 1.25rem; background:linear-gradient(135deg,#0A316E 0%,#221C5D 100%);
      color:#fff; margin-bottom:.9rem;
    }
    .payment-detail .amount-hero .hero-label {
      font-size:.7rem; font-weight:600; letter-spacing:.6px; text-transform:uppercase; opacity:.8;
    }
    .payment-detail .amount-hero .hero-value { font-size:1.65rem; font-weight:800; line-height:1.15; }

    /* Başlıktaki açılır işlem menüsü */
    .payment-detail .actions-menu { border-radius:12px; border:1px solid #e2e8f0; padding:.35rem;
      box-shadow:0 8px 24px rgba(10,49,110,.12); min-width:15rem; }
    .payment-detail .actions-menu .dropdown-item {
      border-radius:8px; font-size:.85rem; font-weight:500; padding:.5rem .7rem;
      display:flex; align-items:center;
    }
    .payment-detail .actions-menu .dropdown-item:hover:not(:disabled) { background:#f1f5f9; }
    .payment-detail .actions-menu .dropdown-item i { font-size:15px; }

    /* Sözleşme metni: sayfa akışını uzatmasın diye sabit yükseklikte kendi
       kutusunda kayar. Satır sonları metinde anlamlı olduğu için pre-wrap. */
    .payment-detail .contract-text {
      max-height:340px; overflow-y:auto;
      white-space:pre-wrap; word-break:break-word;
      background:#f8fafc; border:1px solid #e2e8f0; border-radius:.75rem;
      padding:1rem; font-size:.875rem; line-height:1.55; color:#495057;
    }

    .payment-detail .sub-title {
      font-size:.72rem; font-weight:700; letter-spacing:.5px; text-transform:uppercase;
      color:#94a3b8; border-bottom:1px solid #f0f2f5; padding-bottom:.35rem;
      margin:.35rem 0 .75rem;
    }
    .payment-detail .refund-total {
      display:flex; gap:1.5rem; flex-wrap:wrap; background:#f8fafc; border:1px solid #e2e8f0;
      border-radius:12px; padding:.75rem 1rem; margin-bottom:.85rem;
    }
    .payment-detail .fail-box {
      background:#fff5f5; border:1px solid #fecaca; border-radius:14px;
      padding:.9rem 1.1rem; margin-bottom:1rem; color:#991b1b;
    }
  `;

    if (loading) {
        return (
            <div className="page-content page-common payment-detail">
                <style>{styles}</style>
                <div className="container-fluid">
                    <div className="text-center p-5">
                        <Spinner size="lg" color="primary" />
                        <h6 className="mt-3 text-muted">Yükleniyor...</h6>
                    </div>
                </div>
            </div>
        );
    }

    if (!payment) {
        return (
            <div className="page-content page-common payment-detail">
                <style>{styles}</style>
                <div className="container-fluid">
                    <div className="page-header d-flex align-items-center gap-3">
                        <BackButton onClick={() => (onBack ? onBack() : navigate(-1))} />
                        <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>Ödeme Detayı</h5>
                    </div>
                    <Alert color="warning" className="border-0 rounded-4">
                        {error || "Görüntülenecek ödeme bulunamadı."}
                    </Alert>
                </div>
            </div>
        );
    }

    const statusInfo = getStatusInfo(payment.status);
    const hasRates = (payment?.finalUsdRate > 0) || (payment?.finalEurRate > 0) || (payment?.finalGbpRate > 0);
    const isAccountPayment = payment?.accountId && payment?.accountName;

    const usdApprox = payment.finalUsdRate > 0 ? formatForeignAmount((payment.amount || 0) / payment.finalUsdRate, "USD") : null;
    const eurApprox = payment.finalEurRate > 0 ? formatForeignAmount((payment.amount || 0) / payment.finalEurRate, "EUR") : null;
    const gbpApprox = payment.finalGbpRate > 0 ? formatForeignAmount((payment.amount || 0) / payment.finalGbpRate, "GBP") : null;

    const statChips = [
        {
            label: "Durum",
            value: statusInfo.text,
            icon: statusInfo.icon,
            color: statusInfo.hex,
        },
        {
            label: "Tutar",
            value: formatAmountTRY(payment.amount || 0),
            icon: "bx-purchase-tag",
            color: NAVY,
        },
        {
            label: "Taksit",
            value: payment.receiptType === "CASH"
                ? "Nakit"
                : (payment.installmentCount > 1 ? `${payment.installmentCount} Taksit` : "Tek Çekim"),
            icon: "bx-layer",
            color: ORANGE,
        },
        {
            label: "Muhasebe",
            value: payment.accounted ? "İşlendi" : "İşlenmedi",
            icon: "bx-book-content",
            color: payment.accounted ? GREEN : GRAY,
        },
    ];

    const hasReceiverInfo = !!(payment.companyName || payment.companyPhone || payment.companyEmail
        || payment.companyTaxNumber || payment.companyTaxOffice || payment.companyAddress);
    /* Cari bloğu accountId/accountName şartına BAĞLANMAZ: cari tipi dolu ama adı
       boş kayıtlarda (eski veri) alanın tamamen kaybolmaması için alanlardan
       herhangi biri yeterli. */
    const hasAccountBlock = !!(payment.accountName || payment.accountCode || payment.accountPhone
        || payment.accountAddress || payment.accountType || payment.accountTaxNumber
        || payment.accountTcNumber || payment.accountTaxOffice);
    const hasInvoiceSnapshot = !!(payment.invoiceCompanyName || payment.invoiceTaxNumber
        || payment.invoiceTaxOffice || payment.invoiceTcNumber || payment.invoiceAddress
        || payment.invoiceEmail || payment.invoicePhone || payment.invoiceAccountType);
    const hasRefunds = refundDetail?.refunds?.length > 0;

    return (
        <div className="page-content page-common payment-detail">
            <style>{styles}</style>
            <div className="container-fluid">

                {/* ── Sayfa başlığı ── */}
                <div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-3">
                        <BackButton onClick={() => (onBack ? onBack() : navigate(-1))} />
                        <div>
                            <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>Ödeme Detayı</h5>
                            <span className="text-muted" style={{ fontSize: ".82rem" }}>
                                {payment?.merchantOid || "Ödeme Bilgileri"}
                            </span>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <Badge
                            pill
                            className="px-3 py-2 text-white"
                            style={{ background: statusInfo.hex, fontSize: ".78rem" }}
                        >
                            <i className={`bx ${statusInfo.icon} me-1`} />{statusInfo.text}
                        </Badge>

                        {/* Aksiyonlar durum rozetinin yanında, tek açılır menüde: yan
                            sütundaki buton yığını sayfayı ikiye bölüyor ve kartların
                            genişliğini kırpıyordu. Menü, listedeki satır menüsüyle
                            aynı sırayı izler. */}
                        <UncontrolledDropdown>
                            <DropdownToggle
                                caret
                                className="btn-navy btn-rounded d-flex align-items-center gap-1 px-3"
                                style={{ height: 34, fontSize: ".8rem" }}
                            >
                                <i className="bx bx-slider-alt" style={{ fontSize: 16 }} /> İşlemler
                            </DropdownToggle>
                            <DropdownMenu end className="actions-menu">
                                <DropdownItem onClick={() => viewPaymentReceipt(payment)}>
                                    <i className="bx bx-receipt me-1" /> Makbuz Görüntüle
                                </DropdownItem>

                                <DropdownItem onClick={handleDownloadPdf} disabled={pdfLoading}>
                                    {pdfLoading
                                        ? <Spinner size="sm" className="me-1" />
                                        : <i className="bx bx-download me-1" />}
                                    Makbuz İndir
                                </DropdownItem>

                                <DropdownItem onClick={() => setEmailModalOpen(true)}>
                                    <i className="bx bx-envelope me-1" /> E-posta Gönder
                                </DropdownItem>

                                {canToggleAccounted && (
                                    <>
                                        <DropdownItem divider />
                                        <DropdownItem
                                            onClick={handleToggleAccounted}
                                            disabled={accountingLoading || payment.status !== "SUCCESS"}
                                            title={payment.status !== "SUCCESS"
                                                ? "Sadece başarılı ödemeler muhasebeleştirilebilir"
                                                : ""}
                                        >
                                            {accountingLoading
                                                ? <Spinner size="sm" className="me-1" />
                                                : <i className="bx bx-book-content me-1" />}
                                            {payment.accounted ? "Muhasebeden Çıkar" : "Muhasebeleştir"}
                                        </DropdownItem>
                                    </>
                                )}

                                {/* İade edilemeyen ödemede seçenek gizlenmez, sönük
                                    bırakılır: "iade tuşu nerede?" sorusunu nedeniyle
                                    birlikte yanıtlıyor. */}
                                {canRefund && (
                                    <DropdownItem
                                        onClick={() => setRefundModalOpen(true)}
                                        disabled={!isRefundable}
                                        title={!isRefundable
                                            ? "Yalnızca başarılı ve iade ucu bulunan sanal POS işlemleri iade edilebilir"
                                            : ""}
                                    >
                                        <i className="bx bx-undo me-1" /> Tutar İadesi
                                    </DropdownItem>
                                )}
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    </div>
                </div>

                {error && (
                    <Alert color="danger" className="border-0 rounded-4">
                        <i className="bx bx-error me-2" />
                        {error}
                        <Button size="sm" color="danger" className="ms-2 btn-rounded" onClick={() => window.location.reload()}>
                            Tekrar Dene
                        </Button>
                    </Alert>
                )}

                {/* Üstte kullanıcıya dönük Türkçe açıklama, altında destek ekibi için
                    teknik kod + sağlayıcının ham metni. Teknik alanlar backend'de
                    yalnız SUPER_ADMIN'e gönderilir. */}
                {payment.status === "FAILED"
                    && (payment.failureUserMessage || payment.failedReasonMsg || payment.failedReasonCode) && (
                    <div className="fail-box">
                        <div className="fw-semibold">
                            <i className="bx bx-x-circle me-1" />
                            {payment.failureUserMessage || payment.failedReasonMsg || payment.failedReasonCode}
                        </div>
                        {(payment.failedReasonCode || payment.failedReasonMsg) && (
                            <div className="small mt-2" style={{ opacity: .85, lineHeight: 1.6 }}>
                                {payment.failedReasonCode && (
                                    <div>Hata kodu: <strong>{payment.failedReasonCode}</strong></div>
                                )}
                                {payment.failedReasonMsg
                                    && payment.failedReasonMsg !== payment.failureUserMessage && (
                                    <div>Sağlayıcı mesajı: {payment.failedReasonMsg}</div>
                                )}
                                {payment.virtualPos && <div>Sanal POS: {payment.virtualPosLabel}</div>}
                            </div>
                        )}
                    </div>
                )}

                {/* ── Özet kutucukları ── */}
                <div className="stat-chip-row mb-3">
                    {statChips.map((s, i) => (
                        <div key={i} className="stat-chip">
                            <div className="stat-icon" style={{ background: `${s.color}1A`, color: s.color }}>
                                <i className={`bx ${s.icon}`} />
                            </div>
                            <div className="stat-number" style={{ color: s.color }}>{s.value}</div>
                            <div className="stat-label">{s.label}</div>
                        </div>
                    ))}
                </div>

                <Row className="g-3">
                    {/* Kartlar istenen sırada, tam genişlikte */}
                    <Col xs={12}>

                        {/* 1) Tahsilatı Alan */}
                        <Section icon="bx-buildings" title="Tahsilatı Alan" color={NAVY}>
                            {hasReceiverInfo ? (
                                <Row>
                                    <Field label="Firma Ünvanı" value={payment.companyName} md={4} />
                                    <Field label="Telefon" value={payment.companyPhone} md={4} />
                                    <Field label="E-posta" value={payment.companyEmail} md={4} />
                                    <Field label="Vergi No (VKN)" value={payment.companyTaxNumber} md={4} />
                                    <Field label="Vergi Dairesi" value={payment.companyTaxOffice} md={4} />
                                    {/* Cari detayından girilen nakit/havale tahsilatlarda sanal POS yoktur;
                                        "Bilinmiyor" yazmak yerine alanı hiç göstermiyoruz —
                                        Kart Markası/Tipi alanlarıyla aynı davranış. */}
                                    <Field label="Sanal POS" value={payment.virtualPos ? payment.virtualPosLabel : ""} md={4} />
                                    <Field label="Adres" value={payment.companyAddress} xs={12} md={12} />
                                </Row>
                            ) : (
                                <EmptyHint>Tahsilatı alan firma bilgisi bu kayıtta bulunmuyor.</EmptyHint>
                            )}
                        </Section>

                        {/* 2) Ödemeyi Yapan */}
                        <Section icon="bx-user" title="Ödemeyi Yapan" color={GREEN}>
                            <Row>
                                <Field label="Ad Soyad / Ünvan" value={payment.userNameSnapshot} md={4} />
                                <Field label="E-posta" value={payment.emailSnapshot} md={4} />
                                <Field label="Kullanıcı Tipi" md={4}>
                                    <Badge color={payment.userId ? "success" : "secondary"} className="text-white">
                                        {payment.userId ? "Kayıtlı Kullanıcı" : "Misafir"}
                                    </Badge>
                                </Field>
                                <Field label="Cari / Bayi Ödemesi" md={4}>
                                    <Badge color={isAccountPayment ? "success" : "secondary"} className="text-white">
                                        {isAccountPayment ? "Evet" : "Hayır"}
                                    </Badge>
                                </Field>
                            </Row>

                            {hasAccountBlock && (
                                <>
                                    <div className="sub-title">Cari / Bayi Bilgileri</div>
                                    <Row>
                                        <Field label="Cari / Bayi Adı" value={payment.accountName} md={4} />
                                        <Field label="Cari / Bayi Kodu" value={payment.accountCode} md={4} />
                                        <Field label="Telefon" value={payment.accountPhone} md={4} />
                                        <Field label="Cari / Bayi Tipi" md={4}>
                                            {payment.accountType
                                                ? <Badge color="secondary">{ACCOUNT_TYPE_LABEL_MAP[payment.accountType] || payment.accountType}</Badge>
                                                : undefined}
                                        </Field>
                                        <Field label="Vergi No (VKN)" value={payment.accountTaxNumber} md={4} />
                                        <Field label="Vergi Dairesi" value={payment.accountTaxOffice} md={4} />
                                        <Field label="TC Kimlik No" value={payment.accountTcNumber} md={4} />
                                        <Field label="Adres" value={payment.accountAddress} xs={12} md={12} />
                                    </Row>
                                </>
                            )}

                            {hasInvoiceSnapshot && (
                                <>
                                    <div className="sub-title">Fatura Bilgileri</div>
                                    <Row>
                                        <Field label="Fatura Ünvanı" value={payment.invoiceCompanyName} md={4} />
                                        <Field label="Vergi No (VKN)" value={payment.invoiceTaxNumber} md={4} />
                                        <Field label="TC Kimlik No" value={payment.invoiceTcNumber} md={4} />
                                        <Field label="Vergi Dairesi" value={payment.invoiceTaxOffice} md={4} />
                                        <Field label="Fatura E-posta" value={payment.invoiceEmail} md={4} />
                                        <Field label="Fatura Telefon" value={payment.invoicePhone} md={4} />
                                        <Field label="Fatura Hesap Türü" md={4}>
                                            {payment.invoiceAccountType
                                                ? <Badge color="secondary">{ACCOUNT_TYPE_LABEL_MAP[payment.invoiceAccountType] || payment.invoiceAccountType}</Badge>
                                                : undefined}
                                        </Field>
                                        <Field label="Fatura Adresi" value={payment.invoiceAddress} xs={12} md={12} />
                                    </Row>
                                </>
                            )}
                        </Section>

                        {/* 3) Tahsilat Detayı */}
                        <Section icon="bx-credit-card" title="Tahsilat Detayı" color={ORANGE}>
                            <div className="amount-hero">
                                <div>
                                    <div className="hero-label">
                                        {payment.status === "SUCCESS" ? "Tahsil Edilen Tutar" : "İşlem Tutarı"}
                                    </div>
                                    <div className="hero-value">{formatAmountTRY(payment.amount || 0)}</div>
                                </div>
                                <div className="text-end">
                                    <div className="hero-label">Ödeme Tarihi</div>
                                    <div className="fw-semibold">{formatDateTime(payment.paymentDate)}</div>
                                </div>
                            </div>

                            <Row>
                                <Field label="İşlem Kodu" xs={12} md={4}>
                                    {payment.merchantOid ? <CopyField value={payment.merchantOid} /> : undefined}
                                </Field>
                                <Field label="Tahsilat Tipi" md={4}>
                                    {payment.receiptType ? (
                                        <Badge
                                            style={{
                                                backgroundColor: RECEIPT_TYPE_BADGE_COLORS[payment.receiptType] || "#6c757d",
                                                color: "#fff",
                                            }}
                                        >
                                            {RECEIPT_TYPE_LABEL_MAP[payment.receiptType] || payment.receiptType}
                                        </Badge>
                                    ) : undefined}
                                </Field>
                                <Field label="Taksit" value={installmentText} md={4} />
                                <Field label="Durum" md={4}>
                                    <Badge color={statusInfo.color} className="text-white">{statusInfo.text}</Badge>
                                </Field>
                                <Field
                                    label="Tahsilat Tipi"
                                    value={payment.paymentSource ? getPaymentSourceText(payment.paymentSource) : ""}
                                    md={4}
                                />
                                {/* Cari detayından girilen nakit/havale tahsilatlarda sanal POS yoktur;
                                    "Bilinmiyor" yazmak yerine alanı hiç göstermiyoruz —
                                    Kart Markası/Tipi alanlarıyla aynı davranış. */}
                                <Field label="Sanal POS" value={payment.virtualPos ? payment.virtualPosLabel : ""} md={4} />
                                {/* Kart markası/tipi BIN sorgusundan gelir; elle ve nakit
                                    tahsilatlarda boş olduğu için koşullu gösterilir. */}
                                <Field label="Kart Markası" value={payment.cardBrand ? getCardBrandTr(payment.cardBrand) : ""} md={4} />
                                <Field label="Kart Tipi" value={payment.cardType ? getCardTypeTr(payment.cardType) : ""} md={4} />
                                <Field label="Muhasebeleştirildi" md={4}>
                                    <Badge color={payment.accounted ? "success" : "secondary"} className="text-white">
                                        {payment.accounted ? "Evet" : "Hayır"}
                                    </Badge>
                                </Field>
                            </Row>

                            {hasRates && payment.amount > 0 && (
                                <>
                                    <div className="sub-title">Döviz Karşılıkları (TCMB)</div>
                                    <Row>
                                        <Field label="USD Yaklaşık" value={usdApprox} md={4} />
                                        <Field label="EUR Yaklaşık" value={eurApprox} md={4} />
                                        <Field label="GBP Yaklaşık" value={gbpApprox} md={4} />
                                        <Field label="USD Kuru" value={payment.finalUsdRate > 0 ? `1 $ = ${payment.finalUsdRate} ₺` : ""} md={4} />
                                        <Field label="EUR Kuru" value={payment.finalEurRate > 0 ? `1 € = ${payment.finalEurRate} ₺` : ""} md={4} />
                                        <Field label="GBP Kuru" value={payment.finalGbpRate > 0 ? `1 £ = ${payment.finalGbpRate} ₺` : ""} md={4} />
                                    </Row>
                                </>
                            )}
                        </Section>

                        {/* 4) İade Detayı — başarısız/bekleyen denemeler de listelenir:
                            onlar da denetim kaydıdır ve "neden iade edilemedi" sorusunu yanıtlar. */}
                        {hasRefunds && (
                            <Section
                                icon="bx-undo"
                                title="İade Detayı"
                                color={RED}
                                right={
                                    <Badge pill color={refundDetail.paymentStatus === "REFUNDED" ? "secondary" : "info"} className="text-white">
                                        {refundDetail.paymentStatus === "REFUNDED" ? "Tamamen İade Edildi" : "Kısmi İade"}
                                    </Badge>
                                }
                            >
                                <div className="refund-total">
                                    <div className="info-field mb-0">
                                        <div className="info-label">Toplam İade</div>
                                        <div className="info-value fw-bold" style={{ color: RED }}>
                                            {formatAmountTRY(refundDetail.totalRefunded)}
                                        </div>
                                    </div>
                                    <div className="info-field mb-0">
                                        <div className="info-label">Kalan İade Edilebilir</div>
                                        <div className="info-value fw-bold">
                                            {formatAmountTRY(refundDetail.remainingRefundable)}
                                        </div>
                                    </div>
                                    <div className="info-field mb-0">
                                        <div className="info-label">İade İşlemi</div>
                                        <div className="info-value fw-bold">{refundDetail.refunds.length} adet</div>
                                    </div>
                                </div>

                                <div className="table-responsive">
                                    <table className="table detail-table mb-0">
                                        <thead>
                                            <tr>
                                                <th>Tarih</th>
                                                <th className="text-end">Tutar</th>
                                                <th>Tip</th>
                                                <th>Yapan</th>
                                                <th>Durum</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {refundDetail.refunds.map((r) => (
                                                <tr key={r.refundId}>
                                                    {/* Sonuç zamanı varsa o gösterilir: para gerçekte o an geri döndü. */}
                                                    <td>{formatDateTime(r.completedAt || r.requestedAt)}</td>
                                                    <td className="text-end fw-semibold">{formatAmountTRY(r.amount)}</td>
                                                    <td>
                                                        <Badge color={r.refundClass === "FULL" ? "dark" : "info"} className="text-white">
                                                            {r.refundClass === "FULL" ? "Tam İade" : "Kısmi İade"}
                                                        </Badge>
                                                        {/* VOID tutar kapsamı değil, sağlayıcı işlemidir (aynı gün iptali). */}
                                                        {r.operationType === "VOID" && (
                                                            <Badge color="secondary" className="ms-1 text-white">İptal</Badge>
                                                        )}
                                                    </td>
                                                    <td>{r.createdByName || "-"}</td>
                                                    <td>
                                                        <Badge
                                                            color={r.status === "SUCCESS" ? "success"
                                                                : r.status === "FAILED" ? "danger" : "warning"}
                                                            className="text-white"
                                                        >
                                                            {r.status === "SUCCESS" ? "Başarılı"
                                                                : r.status === "FAILED" ? "Başarısız" : "Bekliyor"}
                                                        </Badge>
                                                        {r.status === "SUCCESS" && r.referenceNo && (
                                                            <div className="text-muted small">Ref: {r.referenceNo}</div>
                                                        )}
                                                        {r.status === "FAILED" && r.failureMessage && (
                                                            <div className="text-muted small">
                                                                {r.failureCode ? `${r.failureCode} — ` : ""}{r.failureMessage}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Section>
                        )}

                        {/* Sözleşme: itiraz/chargeback durumunda "müşteri hangi metni onayladı"
                            sorusunun kanıtı. Metin istendiğinde yüklenir, kendi içinde kayar. */}
                        <Section
                            icon="bx-file"
                            title="Sözleşme Onayı"
                            color={GRAY}
                            right={
                                <Button size="sm" className="btn-navy btn-rounded" onClick={toggleContract} disabled={contractLoading}>
                                    {contractLoading
                                        ? <><Spinner size="sm" /> Yükleniyor…</>
                                        : <>
                                            <i className={`bx ${contractOpen ? "bx-hide" : "bx-show"} me-1`} />
                                            {contractOpen ? "Gizle" : "Sözleşmeyi Görüntüle"}
                                        </>}
                                </Button>
                            }
                        >
                            <EmptyHint>Ödeme sırasında onaylanan mesafeli satış sözleşmesi.</EmptyHint>

                            {contractOpen && !contractLoading && (
                                contract?.contractText ? (
                                    <>
                                        <Row className="mt-3">
                                            <Field label="Onay Tarihi" value={contract.approvedAt ? formatDateTime(contract.approvedAt) : ""} md={4} />
                                            <Field label="Onay IP Adresi" value={contract.contractApprovedIp} md={4} />
                                            <Field label="Metin Özeti (Hash)" xs={12} md={4}>
                                                {contract.contractHash
                                                    ? <span className="text-break font-monospace small">{contract.contractHash}</span>
                                                    : undefined}
                                            </Field>
                                        </Row>
                                        <div className="contract-text mt-2">{contract.contractText}</div>
                                    </>
                                ) : (
                                    <Alert color="light" className="mt-3 mb-0 border">
                                        Bu ödeme için kayıtlı bir sözleşme metni bulunmuyor.
                                    </Alert>
                                )
                            )}
                        </Section>
                    </Col>

                </Row>
            </div>

            <PaymentReceiptEmailModal
                isOpen={emailModalOpen}
                toggle={() => setEmailModalOpen(false)}
                payment={payment}
            />

            <PaymentRefundModal
                isOpen={refundModalOpen}
                toggle={() => setRefundModalOpen(false)}
                payment={payment}
                onConfirm={handleRefundDone}
            />
        </div>
    );
};

export default PaymentDetailPage;
